import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useProfile } from "../../../hooks/useProfile";
import {
  fetchEmployeesAPI,
  fetchArchivedEmployeesAPI,
  fetchCompaniesAPI,
  fetchClientsAPI,
} from "../services/employeeService";
import {
  mapEmployeesData,
  mapArchivedEmployeesData,
  countNewEmployees,
} from "../utils/employeeFormatters";
import { getCached, dropInFlight } from "../utils/employeeCache";

// Clients rarely change within a session — safe to cache a bit longer than
// the fast-moving employee list/counts.
const CLIENTS_TTL_MS = 5 * 60 * 1000;

/**
 * 🪝 useEmployees Hook
 * Hook بسيط يجمع البيانات من service و formatters
 */
export const useEmployees = () => {
  const { profile, profileLoading } = useProfile();
  const clientName = profile?.clientName ?? null;

  // useProfile resolves asynchronously: `clientName` is null on the first
  // render of a cold session and only becomes real once /users/me lands.
  // Because `clientName` is a dependency of fetchAll, firing before it
  // resolves means the whole load runs TWICE -- once against a null client
  // (wrong cache key, and the server still runs the full visibility query),
  // then again for real, with the first one aborted mid-flight.
  //
  // `profileLoading` distinguishes "not fetched yet" from "fetched, and this
  // account genuinely has no client", so waiting here cannot deadlock: it
  // always flips to false, even for a logged-out or client-less account.
  const profileReady = !profileLoading;
  const [employees, setEmployees] = useState([]);
  const [archivedEmployees, setArchivedEmployees] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [clientsMap, setClientsMap] = useState({});
  const [clientsList, setClientsList] = useState([]);
  // Starts true (not false) so the page-level skeleton is guaranteed to be
  // visible on first paint instead of racing the mount effect that flips it.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // This page IS the admin view -- User_Account only mounts AdminRoutes under
  // `viewMode === 'admin'`, and Manager_Account only renders it under
  // `viewType === 'admin'`. The scope is therefore a constant, not something
  // to read back out of localStorage; see the ADMIN-VIEW SCOPING note in
  // services/employeeService.js for why reading it from storage was unsafe.
  //
  // It stays in the cache keys so an entry can never be shared with a
  // differently-scoped fetch if another view is ever added here.
  const viewType = 'admin';

  // Aborts the in-flight list requests when the page unmounts. Without this,
  // navigating away while a large employee list is still downloading leaves
  // the browser holding the connection open and then parsing a response for a
  // screen the user already left -- which is a big part of why leaving this
  // page felt stuck. AbortError is swallowed below rather than surfaced.
  const abortRef = useRef(null);

  // ============== FETCH ALL DATA ==============
  // `forceRefresh` bypasses the cache — pass true right after a mutation
  // (add/edit/delete/import/restore) so the page never shows stale data.
  // Otherwise, a fresh-enough cached result is served instantly, which is
  // what makes revisiting this page (e.g. switching tabs and back) fast
  // instead of re-running 6 API calls every time.
  const fetchAll = useCallback(async (forceRefresh = false) => {
    // Supersede any previous run (e.g. a refresh triggered while the first
    // load is still going) and give this run a fresh signal.
    //
    // dropInFlight() is REQUIRED here, not optional. Aborting leaves the
    // previous run's shared promises sitting in the cache's in-flight map
    // until they asynchronously reject; without dropping them, the getCached
    // calls below are handed those already-aborted promises and this load
    // silently returns nothing.
    //
    // This block sits OUTSIDE the try on purpose: `finally` needs to see
    // `isCurrentRun`, and a `const` declared inside the try block is not in
    // scope there -- referencing it would throw on the way out and leave
    // `loading` stuck true forever, which is a hung page, not a failed load.
    abortRef.current?.abort();
    dropInFlight();
    const controller = new AbortController();
    abortRef.current = controller;
    const { signal } = controller;

    // True only while THIS run is still the newest one. Checked before every
    // state write below: a run that has been superseded must not touch state
    // at all -- not even to report that it finished.
    const isCurrentRun = () => abortRef.current === controller;

    try {
      setLoading(true);
      setError("");

      const cacheOpts = { force: forceRefresh };

      // Always fetch employee data first so the table can render even if counts or clients fail.
      const employeesData = await getCached(
        `employees:${viewType}:${clientName || ""}`,
        () => fetchEmployeesAPI(clientName, signal),
        cacheOpts
      );
      if (!isCurrentRun()) return;
      setEmployees(mapEmployeesData(employeesData));

      // Fetch supporting data in parallel, but treat failures as non-fatal.
      const archivedPromise = getCached(
        `archived:${clientName || ""}`,
        () => fetchArchivedEmployeesAPI(clientName),
        cacheOpts
      ).catch((err) => {
        console.warn("Failed to fetch archived employees:", err);
        return [];
      });
      const companiesPromise = getCached(
        `companies:${viewType}:${clientName || ""}`,
        () => fetchCompaniesAPI(clientName, signal),
        cacheOpts
      ).catch((err) => {
        console.warn("Failed to fetch companies:", err);
        return [];
      });
      const clientsPromise = getCached(
        "clients:all",
        () => fetchClientsAPI(),
        { ...cacheOpts, ttl: CLIENTS_TTL_MS }
      ).catch((err) => {
        console.warn("Failed to fetch clients:", err);
        return { map: {}, list: [] };
      });

      const [
        archivedData,
        companiesData,
        clientsData,
      ] = await Promise.all([
        archivedPromise,
        companiesPromise,
        clientsPromise,
      ]);

      if (!isCurrentRun()) return;
      setArchivedEmployees(mapArchivedEmployeesData(archivedData));
      setCompanies(companiesData);
      setClientsMap(clientsData.map);
      setClientsList(clientsData.list);
    } catch (err) {
      // An abort is not a failure -- it means the user navigated away (or a
      // newer load superseded this one). Surfacing it would flash a bogus
      // error banner on the way out.
      if (err?.name === "AbortError" || !isCurrentRun()) return;
      console.error("❌ Error fetching employees:", err);
      setError("Failed to fetch employees data");
    } finally {
      // Only the newest run may end the loading state.
      //
      // This used to read `if (!abortRef.current?.signal?.aborted)`, which
      // asks "is the CURRENT run aborted" -- not "am I the current run". A
      // superseded run therefore saw the *new* run's fresh controller, decided
      // it was fine, and cleared `loading` while the real fetch was still in
      // flight. That flipped the page out of its skeleton early and let it
      // paint an empty table, with nothing to put it back into a loading state
      // until the newest run happened to land.
      if (isCurrentRun()) setLoading(false);
    }
  }, [clientName, viewType]);

  // Abort any in-flight list request when the page unmounts, so navigating
  // away is immediate instead of waiting on a large download, and no response
  // lands on an unmounted component.
  useEffect(() => () => {
    abortRef.current?.abort();
    // Same reason as in fetchAll: never leave an aborted promise in the shared
    // in-flight map for the next mount to pick up. Completed cache entries are
    // kept, so returning to this page is still instant.
    dropInFlight();
  }, []);

  // ============== COUNTS DERIVED FROM VISIBLE DATA ==============
  // Every stat card number is computed here, from the two payloads the page
  // already holds -- and BOTH of them are scoped server-side to what this
  // caller is allowed to see:
  //
  //   employees        <- GET /employees/visibility  (admin-level rules)
  //   archivedEmployees<- GET /employees/archived    (same rules, archive table)
  //
  // This replaces the old /employees/clients/*/count endpoints, which count
  // every row in the client regardless of admin level. Those counts landed in
  // state AFTER this derivation ran, so on any load where the archived list
  // happened to stay the same length the effect never re-ran and the raw
  // client-wide number was what stayed on the cards -- an admin level scoped
  // to one working company saw the head count of all of them.
  //
  // Deriving instead of fetching also means the cards can never disagree with
  // the table underneath them: same array, same filter.
  const archivedCount = archivedEmployees.length;

  const counts = useMemo(() => {
    let Active = 0;
    let OnLeave = 0;

    employees.forEach((emp) => {
      const status = (emp.status || "").toLowerCase().replace(/\s+/g, "");
      if (status === "active") Active++;
      else if (status === "onleave") OnLeave++;
    });

    return {
      Active,
      OnLeave,
      Recent: 0,
      archived: archivedCount,
      newEmployees: countNewEmployees(employees),
      Total: employees.length,
    };
  }, [employees, archivedCount]);

  // The "Total Employees" card. Same number as counts.Total -- kept as its own
  // name because the page and its callers already read `employeeCount`.
  const employeeCount = counts.Total;

  // ============== INITIAL FETCH ON MOUNT ==============
  useEffect(() => {
    if (!profileReady) return;
    fetchAll();
  }, [fetchAll, profileReady]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleEmployeeDataUpdated = () => {
      // Data actually changed elsewhere — force a real refetch instead of
      // serving the (now stale) cached snapshot.
      fetchAll(true);
    };

    window.addEventListener("employee-data-updated", handleEmployeeDataUpdated);
    return () => {
      window.removeEventListener("employee-data-updated", handleEmployeeDataUpdated);
    };
  }, [fetchAll]);


  // ============== RETURN PUBLIC API ==============
  return {
    // Data
    employees,
    setEmployees,
    archivedEmployees,
    setArchivedEmployees,
    companies,
    clientsMap,
    clientsList,
    employeeCount,
    archivedCount,
    counts,
    selectedEmployee,
    
    // Setters — `counts`, `employeeCount` and `archivedCount` are derived, so
    // they intentionally have no setters: the only way to change them is to
    // change the visibility-scoped data they are computed from.
    setSelectedEmployee,

    // State
    loading,
    setLoading,
    error,
    setError,
    
    // Methods
    fetchAll,
  };
};
